import { useCallback, useLayoutEffect, useMemo, useRef, useState, type MouseEventHandler } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { useMixpanel } from '../../../../contexts';
import { MixpanelEvent, MixpanelEventProperty, MixpanelEventSource } from '../../../../tokens';
import { Stack } from '../../../Core';
import { Icon, IconName } from '../../../Icons';
import { IndicatorBadge, IndicatorBadgeSizes, IndicatorBadgeVariants } from '../../../IndicatorBadge';
import { Text } from '../../../Text';
import { useMobileModal, type ModalConfig } from '../../MobileModalSheet';
import { OverflowModal } from '../OverflowModal';
import { OverflowModalWrapper, OverflowRow, OverflowRowButton } from '../OverflowModal/styles';
import { TabBarAccessories } from '../TabBarAccessories';
import { getEffectiveNavigationPath, type SubTabBarAddOption, type TabBarItem } from '../types';
import { DeleteTabConfirmModal, getRemoveTabTitle } from './DeleteTabConfirmModal';
import {
  SubSelectedIndicatorWrapper,
  SubTabAddButtonElement,
  SubTabBarWrapper,
  SubTabButtonElement,
  SubTabButtonLabel,
  SubTabButtonWrapper,
  SubTabIndicatorWrapper,
  SubTabNavLinkElement,
  SubTabOverflowButtonElement,
} from './styles';
import { TabActionsModal } from './TabActionsModal';

const MOBILE_SUB_TAB_BAR_HORIZONTAL_SPACING = 4;
const MAX_ITEMS_WITHOUT_OVERFLOW = 4;
const VISIBLE_ITEMS_WITH_OVERFLOW = 3;
const MAX_ITEMS_WITH_ADD_WITHOUT_OVERFLOW = 3;

interface SubTabBarProps {
  parent: TabBarItem;
  onNavigate: (path: string) => void;
  addOptions?: SubTabBarAddOption[];
  onRenameItem?: (item: TabBarItem, label: string) => void;
  onDeleteItem?: (item: TabBarItem) => void;
  shouldConfirmDeleteItem?: boolean;
}

export function SubTabBar({
  parent,
  onNavigate,
  addOptions = [],
  onRenameItem,
  onDeleteItem,
  shouldConfirmDeleteItem = true,
}: SubTabBarProps) {
  // Filter out structural children (label undefined); they are routing-only and should not appear in the UI.
  const children = useMemo(
    () => (parent.children ?? []).filter((child: TabBarItem) => child.label != null),
    [parent.children]
  );
  const parentKey = getNavItemKey(parent);
  const storageKey = `mobile-sub-tab-bar-order:${parentKey}`;
  const [savedItemsOrder, setSavedItemsOrder] = useLocalStorage<string[]>(storageKey, []);

  const orderedItems = useMemo(
    () => sortItemsBySavedOrder(children, savedItemsOrder ?? []),
    [children, savedItemsOrder]
  );

  const hasAddTab = addOptions.length > 0;
  const maxItemsWithoutOverflow = hasAddTab ? MAX_ITEMS_WITH_ADD_WITHOUT_OVERFLOW : MAX_ITEMS_WITHOUT_OVERFLOW;
  const hasOverflowItem = orderedItems.length > maxItemsWithoutOverflow;
  const visibleItems = hasOverflowItem ? orderedItems.slice(0, VISIBLE_ITEMS_WITH_OVERFLOW) : orderedItems;
  const overflowCount = hasOverflowItem ? orderedItems.length - visibleItems.length : 0;
  const overflowLabel = `+${overflowCount}`;
  const showAddTabButton = hasAddTab && !hasOverflowItem;

  const location = useLocation();
  const selectedVisibleIndex = visibleItems.findIndex(item => item.path === location.pathname);
  const selectedIndex =
    hasOverflowItem && selectedVisibleIndex === -1
      ? orderedItems.some(item => item.path === location.pathname)
        ? visibleItems.length
        : -1
      : selectedVisibleIndex;

  const barRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLButtonElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const [tabButtonWidth, setTabButtonWidth] = useState(0);
  const [overflowButtonWidth, setOverflowButtonWidth] = useState(0);

  useLayoutEffect(() => {
    const innerBarWidth = (barRef.current?.offsetWidth ?? 0) - MOBILE_SUB_TAB_BAR_HORIZONTAL_SPACING * 2;
    const nextOverflowWidth = hasOverflowItem ? (overflowRef.current?.offsetWidth ?? 0) : 0;
    const nextAddWidth = showAddTabButton ? (addRef.current?.offsetWidth ?? 0) : 0;
    const remainingWidth = Math.max(innerBarWidth - nextOverflowWidth - nextAddWidth, 0);
    setOverflowButtonWidth(nextOverflowWidth);
    setTabButtonWidth(visibleItems.length > 0 ? remainingWidth / visibleItems.length : 0);
  }, [barRef.current?.offsetWidth, hasOverflowItem, showAddTabButton, visibleItems.length, overflowLabel]);

  const indicatorWidth = selectedIndex === visibleItems.length ? overflowButtonWidth : tabButtonWidth;
  const indicatorOffset =
    selectedIndex === visibleItems.length ? visibleItems.length * tabButtonWidth : selectedIndex * tabButtonWidth;

  const mixpanel = useMixpanel(MixpanelEventSource.MobileTabBar);

  const { openModal, closeModal, pushModal, popModal, isModalOpen } = useMobileModal();

  const handleItemsChange = useCallback(
    (nextItems: TabBarItem[]) => {
      setSavedItemsOrder(nextItems.map(getNavItemKey));
    },
    [setSavedItemsOrder]
  );

  const trackNavigateToItem = useCallback(
    (item: TabBarItem) => {
      const effectivePath = getEffectiveNavigationPath(item);
      if (effectivePath == null) {
        return;
      }
      mixpanel.track(MixpanelEvent.NavigateTo, { [MixpanelEventProperty.Path]: effectivePath });
      if (item.mixpanelEvent) {
        mixpanel.track(item.mixpanelEvent);
      }
    },
    [mixpanel]
  );

  const handleAddTab = useCallback(() => {
    if (addOptions.length === 1) {
      addOptions[0].onSelect();
      return;
    }

    openModal({
      content: <AddTabOptionsModal options={addOptions} />,
      title: `Add ${parent.label ?? 'tab'}`,
      useSheetContent: true,
    });
  }, [addOptions, openModal, parent.label]);

  const handleAddTabFromOverflow = useCallback(() => {
    if (addOptions.length === 1) {
      addOptions[0].onSelect();
      closeModal();
      return;
    }

    pushModal({
      content: <AddTabOptionsModal options={addOptions} />,
      title: `Add ${parent.label ?? 'tab'}`,
      useSheetContent: true,
    });
  }, [addOptions, closeModal, parent.label, pushModal]);

  const openBottomSheet = useCallback(
    (params: Omit<ModalConfig, 'bottomSheet' | 'bottomSheetHeight'>) => {
      openModal({ ...params, useSheetContent: true, bottomSheet: true, bottomSheetHeight: 240 });
    },
    [openModal]
  );

  const pushBottomSheet = useCallback(
    (params: Omit<ModalConfig, 'bottomSheet' | 'bottomSheetHeight'>) => {
      pushModal({ ...params, useSheetContent: true, bottomSheet: true, bottomSheetHeight: 240 });
    },
    [pushModal]
  );

  const handleOpenTabActions = useCallback(
    (item: TabBarItem) => {
      if (item.isEditable !== true && item.isRemovable !== true) {
        return;
      }

      openBottomSheet({
        content: (
          <TabActionsModal
            item={item}
            onRenameItem={onRenameItem}
            onDeleteItem={onDeleteItem}
            shouldConfirmDeleteItem={shouldConfirmDeleteItem}
          />
        ),
        title: item.isEditable === true ? 'Rename tab' : (item.label ?? 'Tab'),
      });
    },
    [onDeleteItem, onRenameItem, openBottomSheet, shouldConfirmDeleteItem]
  );
  const handleOpenOverflowTabActions = useCallback(
    (item: TabBarItem) => {
      if (item.isEditable !== true && item.isRemovable !== true) {
        return;
      }

      pushBottomSheet({
        content: (
          <TabActionsModal
            item={item}
            onRenameItem={onRenameItem}
            onDeleteItem={onDeleteItem}
            shouldConfirmDeleteItem={shouldConfirmDeleteItem}
          />
        ),
        title: item.isEditable === true ? 'Rename tab' : (item.label ?? 'Tab'),
      });
    },
    [onDeleteItem, onRenameItem, pushBottomSheet, shouldConfirmDeleteItem]
  );
  const handleOpenOverflowDeleteConfirm = useCallback(
    (item: TabBarItem, onConfirmDelete: () => void) => {
      if (item.isRemovable !== true || onDeleteItem == null) {
        return;
      }

      if (!shouldConfirmDeleteItem) {
        onDeleteItem(item);
        onConfirmDelete();
        return;
      }

      pushBottomSheet({
        content: (
          <DeleteTabConfirmModal
            item={item}
            onDeleteItem={onDeleteItem}
            onConfirmDelete={() => {
              onConfirmDelete();
              popModal();
            }}
          />
        ),
        title: getRemoveTabTitle(item),
      });
    },
    [onDeleteItem, popModal, pushBottomSheet, shouldConfirmDeleteItem]
  );

  const handleOpenOverflow = useCallback(() => {
    if (isModalOpen) {
      closeModal();
      return;
    }
    openModal({
      content: (
        <OverflowModal
          items={orderedItems}
          onItemsChange={handleItemsChange}
          onNavigate={onNavigate}
          onAddTab={hasAddTab ? handleAddTabFromOverflow : undefined}
          onOpenItemActions={handleOpenOverflowTabActions}
          onOpenDeleteConfirm={handleOpenOverflowDeleteConfirm}
        />
      ),
      title: parent.label,
      useSheetContent: true,
    });
    mixpanel.track(MixpanelEvent.OpenMobileTabBarOverflow);
  }, [
    openModal,
    closeModal,
    isModalOpen,
    orderedItems,
    handleItemsChange,
    parent.label,
    mixpanel,
    onNavigate,
    hasAddTab,
    handleAddTabFromOverflow,
    handleOpenOverflowTabActions,
    handleOpenOverflowDeleteConfirm,
  ]);

  if (children.length === 0 && !hasAddTab) {
    return null;
  }

  return (
    <TabBarAccessories>
      <SubTabBarWrapper ref={barRef} data-testid="mobile-sub-tab-bar">
        {selectedIndex >= 0 && (
          <SubSelectedIndicatorWrapper w={indicatorWidth} transform={`translateX(${indicatorOffset}px)`} />
        )}
        {visibleItems.map((item, index) => (
          <SubTabButton
            key={getNavItemKey(item)}
            item={item}
            isSelected={index === selectedIndex}
            onPress={() => trackNavigateToItem(item)}
            onOpenActions={() => handleOpenTabActions(item)}
          />
        ))}
        {hasOverflowItem && (
          <SubTabOverflowButtonElement ref={overflowRef} type="button" onClick={handleOpenOverflow}>
            <SubTabButtonWrapper isSelected={selectedIndex === visibleItems.length}>
              <SubTabButtonLabel data-testid="mobile-sub-tab-overflow-label">{overflowLabel}</SubTabButtonLabel>
            </SubTabButtonWrapper>
          </SubTabOverflowButtonElement>
        )}
        {showAddTabButton && (
          <SubTabAddButtonElement
            ref={addRef}
            type="button"
            aria-label="New tab"
            onClick={handleAddTab}
            data-testid="mobile-sub-tab-add-button"
          >
            <SubTabButtonWrapper gap="spacing4">
              <Icon icon={IconName.Plus} size={16} />
              <SubTabButtonLabel>New tab</SubTabButtonLabel>
            </SubTabButtonWrapper>
          </SubTabAddButtonElement>
        )}
      </SubTabBarWrapper>
    </TabBarAccessories>
  );
}

function AddTabOptionsModal({ options }: { options: SubTabBarAddOption[] }) {
  const { closeModal } = useMobileModal();

  const handleSelect = useCallback(
    (option: SubTabBarAddOption) => {
      option.onSelect();
      closeModal();
    },
    [closeModal]
  );

  return (
    <OverflowModalWrapper>
      <Stack gap="spacing4">
        {options.map(option => (
          <OverflowRow key={option.id}>
            <OverflowRowButton
              type="button"
              onClick={() => handleSelect(option)}
              data-testid={`mobile-sub-tab-add-option-${option.id}`}
            >
              <Icon size={20} icon={option.icon ?? IconName.Plus} />
              <Text
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                flex="1 1 0"
                color="colorTextImportant"
              >
                {option.label}
              </Text>
            </OverflowRowButton>
          </OverflowRow>
        ))}
      </Stack>
    </OverflowModalWrapper>
  );
}

function SubTabButton({
  item,
  isSelected,
  onPress,
  onOpenActions,
}: {
  item: TabBarItem;
  isSelected: boolean;
  onPress?: () => void;
  onOpenActions?: () => void;
}) {
  const showIndicator = item.indicatorCount != null && item.indicatorCount > 0;
  const handleContextMenu = useCallback<MouseEventHandler>(
    event => {
      event.preventDefault();
      onOpenActions?.();
    },
    [onOpenActions]
  );
  const content = (
    <SubTabButtonWrapper isSelected={isSelected}>
      <SubTabButtonLabel data-testid={getSubTabLabelTestId(item)}>{item.label}</SubTabButtonLabel>
      {showIndicator && (
        <SubTabIndicatorWrapper>
          <IndicatorBadge
            size={IndicatorBadgeSizes.Small}
            variant={IndicatorBadgeVariants.Notification}
            data-testid={getSubTabIndicatorTestId(item)}
          >
            {item.indicatorCount}
          </IndicatorBadge>
        </SubTabIndicatorWrapper>
      )}
    </SubTabButtonWrapper>
  );

  const effectivePath = getEffectiveNavigationPath(item);

  if (effectivePath == null) {
    return (
      <SubTabButtonElement type="button" onClick={onPress} onContextMenu={handleContextMenu}>
        {content}
      </SubTabButtonElement>
    );
  }

  return (
    <SubTabNavLinkElement key={item.id} to={effectivePath} onClick={onPress} onContextMenu={handleContextMenu}>
      {content}
    </SubTabNavLinkElement>
  );
}

function getNavItemKey(item: TabBarItem) {
  return item.id ?? item.path ?? '';
}

function getSubTabLabelTestId(item: TabBarItem) {
  return `mobile-sub-tab-label-${getNavItemKey(item)}`;
}

function getSubTabIndicatorTestId(item: TabBarItem) {
  return `mobile-sub-tab-indicator-${getNavItemKey(item)}`;
}

function sortItemsBySavedOrder(items: TabBarItem[], savedItemsOrder: string[]) {
  if (savedItemsOrder.length === 0) {
    return items;
  }

  const positions = new Map(savedItemsOrder.map((id, index) => [id, index]));
  return items.toSorted((a, b) => {
    const positionA = positions.get(getNavItemKey(a)) ?? Number.MAX_SAFE_INTEGER;
    const positionB = positions.get(getNavItemKey(b)) ?? Number.MAX_SAFE_INTEGER;
    return positionA - positionB;
  });
}
